import { useCallback, useEffect, useState } from 'react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, MetricCard, KpiStrip, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { CollectedAt, MomentumBadge, SideBadge } from '@/features/dossier/components/primitives'
import { getLiveFeed, type LiveFeed, type LiveFeedRow } from './api'

/**
 * The head-to-head.
 *
 * ## Two rules, and the screen is mostly them
 *
 * **No publishable estimate is printed as such, never as 0%.** An unmeasured candidate and a
 * candidate at zero are different facts, and a bar chart cannot tell them apart — so a row
 * without a share renders its REASON instead of a bar.
 *
 * **Every share carries its base-n and its margin, on the row.** A head-to-head with no
 * base-n is how a campaign talks itself into a strategy on forty interviews.
 *
 * And the consequence: a lead smaller than the combined margin says **too close to call**,
 * not "ahead". That is the single most useful sentence this screen can print, and it is the
 * one an ordinary dashboard leaves out.
 */
export function LiveFeedPage() {
  const [feed, setFeed] = useState<LiveFeed | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setFeed(await getLiveFeed())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !feed) return <ErrorStrip message={error} />
  if (!feed) return <LoadingBlock label="Reading the polling" />

  const measured = feed.rows.filter((r) => r.share != null)
  const widest = Math.max(0.01, ...measured.map((r) => r.share ?? 0))

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Live Survey Feed"
        description="Our share against the field. Every figure carries the base and the margin
                     that qualify it."
      />

      <KpiStrip>
        <MetricCard label="Our share"
                    value={feed.ours?.share == null ? '—' : pct(feed.ours.share)}
                    sub={feed.ours?.baseN ? `n=${feed.ours.baseN} · ${feed.ours.waveLabel ?? ''}`
                      : 'no publishable estimate'} />
        <MetricCard label="Strongest opponent"
                    value={feed.topOpponent?.share == null ? '—' : pct(feed.topOpponent.share)}
                    sub={feed.topOpponent?.name ?? 'none measured'} />
        <MetricCard
          label={feed.withinMargin ? 'Too close to call' : 'Lead'}
          value={feed.leadPp == null ? '—' : `${feed.leadPp > 0 ? '+' : ''}${feed.leadPp}pp`}
          sub={feed.withinMargin
            ? 'Smaller than the combined margin of error — this is not a lead'
            : feed.leadPp == null ? 'Needs both sides measured' : 'Outside the combined margin'}
        />
        <MetricCard label="Unmeasured" value={feed.unmeasured}
                    sub="Shown with a reason, never as 0%" />
      </KpiStrip>

      <Panel>
        <ErpTable head={<>
          <Th>Target</Th>
          <Th>Side</Th>
          <Th>Share</Th>
          <Th className="text-right">Margin</Th>
          <Th className="text-right">Base</Th>
          <Th className="axis-hide-sm">Wave</Th>
          <Th>Momentum</Th>
          <Th className="axis-hide-sm">Computed</Th>
        </>}>
          {feed.rows.length === 0 && (
            <EmptyRow colSpan={8} message="No targets to compare." />
          )}
          {feed.rows.map((row) => (
            <tr key={row.targetId} className="hover:bg-muted/40">
              <Td><span className="font-medium">{row.name}</span></Td>
              <Td><SideBadge side={row.sideCode} /></Td>
              <Td>
                {row.share == null ? (
                  // The REASON, not a bar. An empty bar reads as zero.
                  <span className="text-[11px] text-muted-foreground"
                        title={row.noEstimateReason ?? undefined}>
                    no publishable estimate
                  </span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="w-24 rounded-full bg-muted">
                      <span className="block h-1.5 rounded-full bg-primary"
                            style={{ width: `${((row.share / widest) * 100).toFixed(0)}%` }} />
                    </span>
                    <span className="tabular-nums">{pct(row.share)}</span>
                  </div>
                )}
              </Td>
              <Td className="text-right tabular-nums">
                {row.moe95 == null ? '—' : `±${pct(row.moe95)}`}
              </Td>
              <Td className="text-right tabular-nums">{row.baseN ?? '—'}</Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">{row.waveLabel ?? '—'}</span>
              </Td>
              <Td><MomentumBadge momentum={row.momentumCode} /></Td>
              <Td className="axis-hide-sm"><CollectedAt at={row.computedAt} /></Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      {feed.rows.some((r) => r.share == null) && (
        <Panel>
          <div className="space-y-1 p-2">
            <p className="text-[11px] font-medium">Why some rows have no figure</p>
            {feed.rows.filter((r) => r.share == null).map((r: LiveFeedRow) => (
              <p key={r.targetId} className="text-[11px] text-muted-foreground">
                <strong>{r.name}</strong> — {r.noEstimateReason}
              </p>
            ))}
          </div>
        </Panel>
      )}
    </div>
  )
}

function pct(proportion: number): string {
  return `${(proportion * 100).toFixed(1)}%`
}
